import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** How long a finger has to rest on the marquee, in ms, before it counts as a press. */
const PRESS_DELAY = 200;

/**
 * Generic continuous "marquee" strip: a constant-speed, infinite translateX
 * loop (Web Animations API) over a row of content, instead of the discrete
 * step-and-wait autoplay `<slideshow-component>` provides. Content-agnostic
 * -- used by sections/testimonial-slider.liquid and
 * sections/instagram-media-feed.liquid, and safe to reuse anywhere else a
 * continuously flowing row of cards is wanted.
 *
 * Expects a single `ref="track"` element holding the row in its first child
 * group, optionally followed by a second copy of it. Animating the track from
 * the start of the first group to the start of the second means the second
 * (aria-hidden) group seamlessly picks up where the first ends -- a standard
 * seamless-marquee technique. If the first group starts out narrower than the
 * marquee itself (short content on a wide banner), it's padded out with
 * repeated copies of its original children before the loop starts, so
 * there's always more than one screen's worth of content ahead of the visible
 * edge and the loop never runs dry.
 *
 * Pauses on hover, keyboard focus, touch/pointer press, when the tab is hidden
 * and when it's off screen; resumes automatically once nothing is holding it
 * paused anymore. Nothing starts until it first nears the screen, and the second
 * group is only built then if the markup doesn't already include one, with just
 * enough cards to cover the marquee's width.
 *
 * Falls back to a plain swipeable/scrollable row -- no animation, single
 * card group only -- when `prefers-reduced-motion` is set, or when the
 * `static` attribute is present (autoplay turned off in section settings).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 *
 * @extends {Component<Refs>}
 */
export class Marquee extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();

    const group = this.refs.track.firstElementChild;
    if (!(group instanceof HTMLElement)) return;
    this.#group = group;

    if (this.#isStatic) {
      this.setAttribute('reduced-motion', '');
      return;
    }

    this.#viewObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          this.#pause('offscreen');
          return;
        }

        if (!this.#animation) this.#start();
        this.#resume('offscreen');
      },
      { rootMargin: '200px 0px' }
    );
    this.#viewObserver.observe(this);
  }

  #start() {
    if (!this.#group) return;

    this.#ensureMinimumWidth();
    this.#fillClone();
    this.#neutralizeClone();
    this.#startAnimation();
    if (document.hidden) this.#pause('hidden');

    this.#resizeObserver = new ResizeObserver(() => this.#handleResize());
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.#group);

    if (this.#pauseOnHover) {
      this.addEventListener('mouseenter', this.#handleHoverStart);
      this.addEventListener('mouseleave', this.#handleHoverEnd);
    }
    this.addEventListener('focusin', this.#handleHoverStart);
    this.addEventListener('focusout', this.#handleHoverEnd);
    this.addEventListener('pointerdown', this.#handlePressStart);
    this.addEventListener('touchstart', this.#handlePressStart, { passive: true });
    this.addEventListener('pointerup', this.#handlePressEnd);
    this.addEventListener('pointercancel', this.#handlePressEnd);
    this.addEventListener('touchend', this.#handlePressEnd, { passive: true });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.hasAttribute('swipeable')) {
      // Let the browser keep vertical page scrolling; horizontal drags come to us
      this.style.touchAction = 'pan-y';
      this.addEventListener('pointerdown', this.#handleDragStart);
      this.addEventListener('pointermove', this.#handleDragMove);
      this.addEventListener('pointerup', this.#handleDragEnd);
      this.addEventListener('pointercancel', this.#handleDragEnd);
      this.addEventListener('click', this.#handleClickAfterDrag, { capture: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#animation?.cancel();
    this.#animation = undefined;
    this.#viewObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#resumeTimer);
    clearTimeout(this.#pressTimer);

    this.removeEventListener('mouseenter', this.#handleHoverStart);
    this.removeEventListener('mouseleave', this.#handleHoverEnd);
    this.removeEventListener('focusin', this.#handleHoverStart);
    this.removeEventListener('focusout', this.#handleHoverEnd);
    this.removeEventListener('pointerdown', this.#handlePressStart);
    this.removeEventListener('touchstart', this.#handlePressStart);
    this.removeEventListener('pointerup', this.#handlePressEnd);
    this.removeEventListener('pointercancel', this.#handlePressEnd);
    this.removeEventListener('touchend', this.#handlePressEnd);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    cancelAnimationFrame(this.#glideFrame);
    this.removeEventListener('pointerdown', this.#handleDragStart);
    this.removeEventListener('pointermove', this.#handleDragMove);
    this.removeEventListener('pointerup', this.#handleDragEnd);
    this.removeEventListener('pointercancel', this.#handleDragEnd);
    this.removeEventListener('click', this.#handleClickAfterDrag, { capture: true });
  }

  /** Advances one card, briefly pausing autoplay so the visitor has time to read it. */
  next() {
    this.#nudge(1);
  }

  /** Goes back one card, briefly pausing autoplay. */
  previous() {
    this.#nudge(-1);
  }

  /** @type {Animation | undefined} */
  #animation;

  /** @type {HTMLElement | undefined} */
  #group;

  /** Current loop duration in ms -- one full pass over a single card group. */
  #duration = 0;

  /** Pixels the track travels in one loop: the start of the first group to the start of the second. */
  #travel = 0;

  /** True when the second group was built here, so it can be topped up after a resize. */
  #builtClone = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {IntersectionObserver | undefined} */
  #viewObserver;

  /** Reasons currently holding the marquee paused; it only resumes once this is empty. */
  #pauseReasons = new Set();

  /** @type {number | undefined} */
  #resumeTimer;

  /** @type {number | undefined} */
  #pressTimer;

  /** Pixels the marquee travels per second. */
  get speed() {
    const value = parseFloat(this.getAttribute('speed') ?? '');
    return Number.isFinite(value) && value > 0 ? value : 50;
  }

  /** True when the marquee should never animate: reduced motion, or autoplay disabled in section settings. */
  get #isStatic() {
    return this.hasAttribute('static') || prefersReducedMotion();
  }

  /** False only when explicitly opted out via `pause-on-hover="false"`; pausing on hover is the default. */
  get #pauseOnHover() {
    return this.getAttribute('pause-on-hover') !== 'false';
  }

  /**
   * Builds the second group for markup that ships only one, copying just enough
   * cards to cover the marquee's width. That's all of it the loop ever shows, and
   * a full copy of a long row doubles the elements the browser has to work through
   * every time something on the page changes. Called again after a resize, in case
   * the marquee got wider.
   */
  #fillClone() {
    const group = this.#group;
    if (!group) return;

    let clone = this.refs.track.children[1];
    if (clone && !this.#builtClone) return;

    if (!clone) {
      const empty = group.cloneNode(false);
      if (!(empty instanceof HTMLElement)) return;

      empty.setAttribute('aria-hidden', 'true');
      this.refs.track.append(empty);
      this.#builtClone = true;
      clone = empty;
    }

    const cards = Array.from(group.children);
    const start = group.getBoundingClientRect().left;
    const width = this.getBoundingClientRect().width;
    const lastNeeded = cards.findIndex((card) => card.getBoundingClientRect().right - start >= width);
    const needed = lastNeeded === -1 ? cards.length : lastNeeded + 1;

    for (const card of cards.slice(clone.children.length, needed)) {
      clone.append(card.cloneNode(true));
    }
  }

  /**
   * The seamless loop only works while the first group is at least as wide
   * as the marquee itself -- otherwise both groups fit on screen at once and,
   * once the translate carries the first group fully offscreen, there's
   * nothing left to show until the loop resets, i.e. a visible blank gap
   * instead of an infinite scroll. Short content (a one-line topbar
   * message, say) routinely doesn't fill a full-width banner on a wide
   * viewport, so pad it out by repeating its original children (in a shipped
   * second group too) until it does, before the width is ever measured for
   * the animation.
   */
  #ensureMinimumWidth() {
    if (!this.#group) return;

    const clone = this.#builtClone ? null : this.refs.track.children[1];

    const originalChildren = Array.from(this.#group.children);
    if (!originalChildren.length) return;

    const containerWidth = this.getBoundingClientRect().width;
    if (!containerWidth) return;

    // Guards against spinning forever if width can't ever be measured (e.g.
    // the element is hidden) -- 20 passes covers even a single short word
    // on the widest realistic viewport many times over.
    let guard = 0;
    while (this.#group.getBoundingClientRect().width < containerWidth && guard < 20) {
      for (const child of originalChildren) {
        this.#group.appendChild(child.cloneNode(true));
        clone?.appendChild(child.cloneNode(true));
      }
      guard++;
    }
  }

  /**
   * If the second (aria-hidden) card group ships in the markup, it has an
   * `inert` attribute -- a safe, no-JS default that keeps it out of the tab
   * order and unclickable. But once the marquee is actually animating, either
   * copy can be the one currently sitting on screen at any given moment (the
   * loop just keeps handing off between them), and `inert` disables *all*
   * interaction, pointer included -- so hover effects (e.g. the Instagram
   * icon overlay) would silently stop working for however long the clone
   * happens to be the visible copy. Swap `inert` for per-link `tabindex`
   * instead: keyboard users still can't tab into the duplicate and screen
   * readers still skip it (aria-hidden alone handles that), but pointer
   * hover/click keep working on it exactly like the real copy.
   */
  #neutralizeClone() {
    const clone = this.refs.track.children[1];
    if (!(clone instanceof HTMLElement)) return;

    clone.removeAttribute('inert');
    clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
  }

  /** The start of the first group to the start of the second, in pixels. */
  #measureTravel() {
    const clone = this.refs.track.children[1];
    if (!this.#group || !clone) return 0;

    return clone.getBoundingClientRect().left - this.#group.getBoundingClientRect().left;
  }

  /** Keyframes for one loop. Reversed, the track starts shifted so the second group is what's on screen. */
  get #keyframes() {
    const frames = [{ transform: 'translateX(0px)' }, { transform: `translateX(${-this.#travel}px)` }];
    return this.hasAttribute('reverse') ? frames.reverse() : frames;
  }

  #startAnimation() {
    this.#travel = this.#measureTravel();
    if (!this.#travel) return;

    this.#duration = (this.#travel / this.speed) * 1000;
    this.#animation = this.refs.track.animate(this.#keyframes, {
      duration: this.#duration,
      iterations: Infinity,
      easing: 'linear',
    });
  }

  #handleResize() {
    if (!this.#animation || !this.#duration) return;

    this.#ensureMinimumWidth();
    this.#fillClone();
    this.#neutralizeClone();

    const travel = this.#measureTravel();
    if (!travel || travel === this.#travel) return;

    const progress = (((this.#animation.currentTime ?? 0) / this.#duration) % 1 + 1) % 1;
    this.#travel = travel;
    this.#duration = (travel / this.speed) * 1000;

    if (this.#animation.effect instanceof KeyframeEffect) this.#animation.effect.setKeyframes(this.#keyframes);
    this.#animation.effect?.updateTiming({ duration: this.#duration });
    this.#animation.currentTime = progress * this.#duration;
  }

  /** @param {1 | -1} step */
  #nudge(step) {
    const group = this.#group;
    if (!group) return;

    if (this.#isStatic) {
      const cardCount = group.children.length || 1;
      const distance = group.getBoundingClientRect().width / cardCount;
      this.refs.track.scrollBy({ left: step * distance, behavior: 'smooth' });
      return;
    }

    if (!this.#animation || !this.#duration) return;

    const cardCount = group.children.length || 1;
    const slice = this.#duration / cardCount;
    const current = this.#animation.currentTime ?? 0;

    this.#animation.currentTime = (((current + step * slice) % this.#duration) + this.#duration) % this.#duration;

    this.#pause('interaction');
    this.#scheduleResume();
  }

  /**
   * Swipe support (opt-in via the `swipeable` attribute): a horizontal
   * touch/pen drag scrubs the running loop by moving the animation's
   * currentTime, so the row follows the finger and wraps seamlessly just
   * like the autoplay does. Releasing mid-flick glides with decaying
   * velocity, then autoplay resumes through the usual resume timer. Mouse
   * users keep hover-pause and the arrow buttons instead.
   *
   * @type {{ pointerId: number, startX: number, startY: number, lastX: number, lastTime: number, velocity: number, dragging: boolean } | null}
   */
  #drag = null;

  /** @type {number | undefined} */
  #glideFrame;

  /** Set after a drag so the click that ends it doesn't open a card link. */
  #suppressClick = false;

  /** @param {PointerEvent} event */
  #handleDragStart = (event) => {
    if (event.pointerType === 'mouse' || !this.#animation || !this.#duration) return;

    cancelAnimationFrame(this.#glideFrame);
    this.#suppressClick = false;
    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0,
      dragging: false,
    };
  };

  /** @param {PointerEvent} event */
  #handleDragMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    if (!drag.dragging) {
      const totalX = event.clientX - drag.startX;
      const totalY = event.clientY - drag.startY;
      // Wait for a clearly horizontal gesture before taking it over
      if (Math.abs(totalX) < 6 || Math.abs(totalX) < Math.abs(totalY)) return;

      drag.dragging = true;
      drag.lastX = event.clientX;
      drag.lastTime = event.timeStamp;
      this.setPointerCapture(event.pointerId);
      this.#pause('press');
      return;
    }

    const deltaX = event.clientX - drag.lastX;
    const elapsed = Math.max(event.timeStamp - drag.lastTime, 1);

    this.#scrubBy(deltaX);
    drag.velocity = deltaX / elapsed;
    drag.lastX = event.clientX;
    drag.lastTime = event.timeStamp;
  };

  /** @param {PointerEvent} event */
  #handleDragEnd = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.#drag = null;
    if (!drag.dragging) return;

    this.#suppressClick = true;
    // A pause at the end of the drag means no flick, so no glide
    const velocity = event.timeStamp - drag.lastTime > 80 ? 0 : drag.velocity;
    this.#glide(velocity);
  };

  /** @param {MouseEvent} event */
  #handleClickAfterDrag = (event) => {
    if (!this.#suppressClick) return;

    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Moves the row by `deltaX` pixels (positive = toward the right) by
   * shifting the loop's currentTime, wrapping within one loop.
   * @param {number} deltaX
   */
  #scrubBy(deltaX) {
    if (!this.#animation || !this.#duration || !this.#travel) return;

    // One loop moves the track leftward unless reversed
    const direction = this.hasAttribute('reverse') ? 1 : -1;
    const deltaTime = (deltaX / (direction * this.#travel)) * this.#duration;
    const current = Number(this.#animation.currentTime ?? 0);

    this.#animation.currentTime = (((current + deltaTime) % this.#duration) + this.#duration) % this.#duration;
  }

  /**
   * Continues a flick with decaying velocity (px/ms), then hands back to autoplay.
   * @param {number} velocity
   */
  #glide(velocity) {
    clearTimeout(this.#resumeTimer);
    this.#pause('interaction');

    let speed = velocity;
    let previous = performance.now();

    const step = (/** @type {number} */ now) => {
      const elapsed = Math.min(now - previous, 50);
      previous = now;
      this.#scrubBy(speed * elapsed);
      speed *= Math.pow(0.95, elapsed / 16);

      if (Math.abs(speed) > 0.02) {
        this.#glideFrame = requestAnimationFrame(step);
      } else {
        this.#scheduleResume();
      }
    };

    this.#glideFrame = requestAnimationFrame(step);
  }

  #handleHoverStart = () => this.#pause('hover');
  #handleHoverEnd = () => this.#resume('hover');

  /** @param {PointerEvent | TouchEvent} event */
  #handlePressStart = (event) => {
    clearTimeout(this.#resumeTimer);
    clearTimeout(this.#pressTimer);

    // A finger here may just be on its way to scrolling the page, and pausing makes
    // the browser rebuild the page's layers, so wait to see that it stays put
    const isTouch = event.type === 'touchstart' || ('pointerType' in event && event.pointerType === 'touch');
    if (isTouch) this.#pressTimer = window.setTimeout(() => this.#pause('press'), PRESS_DELAY);
    else this.#pause('press');
  };

  #handlePressEnd = () => {
    clearTimeout(this.#pressTimer);
    if (this.#pauseReasons.has('press')) this.#scheduleResume();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) this.#pause('hidden');
    else this.#resume('hidden');
  };

  #scheduleResume() {
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = window.setTimeout(() => {
      this.#pauseReasons.delete('press');
      this.#pauseReasons.delete('interaction');
      this.#resume();
    }, 1300);
  }

  /** @param {string} reason */
  #pause(reason) {
    this.#pauseReasons.add(reason);
    this.#animation?.pause();
  }

  /** @param {string} [reason] */
  #resume(reason) {
    if (reason) this.#pauseReasons.delete(reason);
    if (this.#pauseReasons.size) return;
    this.#animation?.play();
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', Marquee);
}
